import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const fmtWeek = (iso) =>
  new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export default function GbpPanel() {
  const [posts, setPosts] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState({});

  const load = () => api.get("/gbp-posts").then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/gbp-posts/generate", { weeks: 4 });
      toast.success(`${r.data.created} weekly post(s) written by AI`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const copyPost = (p) => {
    navigator.clipboard.writeText(`${editing[p.id] ?? p.post_text}\n\n${p.cta_url}`);
    toast.success("Post copied — paste it into your Google Business Profile");
  };

  const save = async (p, status) => {
    const payload = {};
    if (editing[p.id] !== undefined && editing[p.id] !== p.post_text) payload.post_text = editing[p.id];
    if (status) payload.status = status;
    if (!Object.keys(payload).length) return;
    await api.put(`/gbp-posts/${p.id}`, payload);
    toast.success(status === "posted" ? "Marked as posted" : "Saved");
    load();
  };

  const remove = async (p) => {
    await api.delete(`/gbp-posts/${p.id}`);
    load();
  };

  const thisMonday = (() => {
    const d = new Date();
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return d.toISOString().slice(0, 10);
  })();

  return (
    <div data-testid="gbp-panel">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h2 className="font-display text-3xl text-ink">Google Business Profile — weekly posts</h2>
          <p className="text-ink-soft mt-2 max-w-2xl">
            AI writes one post per week from your blog. Copy it, open your Google Business Profile app
            (Promote → Add update), paste, attach the suggested photo, and mark it posted. 30 seconds a week.
          </p>
        </div>
        <Button onClick={generate} disabled={generating} className="btn-ember !rounded-none" data-testid="gbp-generate-btn">
          {generating ? "AI writing…" : "Generate next 4 weeks"}
        </Button>
      </div>

      {posts.length === 0 && (
        <p className="text-ink-muted font-mono-label" data-testid="gbp-empty">No posts yet — click "Generate next 4 weeks".</p>
      )}

      <div className="space-y-6">
        {posts.map((p) => (
          <article key={p.id} className={`border p-6 ${p.week_start === thisMonday ? "border-ember bg-ember-light/40" : "border-rule-subtle bg-sand/40"}`} data-testid={`gbp-post-${p.week_start}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <p className="font-display text-xl text-ink">
                  Week of {fmtWeek(p.week_start)}
                  {p.week_start === thisMonday && <span className="ml-3 font-mono-label text-ember">← this week</span>}
                </p>
                <p className="font-mono-label text-ink-muted mt-1">Source: {p.blog_title}</p>
              </div>
              <span className={`font-mono-label px-3 py-1 ${p.status === "posted" ? "bg-ink text-canvas" : "bg-dune text-ink-soft"}`} data-testid={`gbp-status-${p.week_start}`}>
                {p.status === "posted" ? "Posted ✓" : "Draft"}
              </span>
            </div>
            <Textarea
              value={editing[p.id] ?? p.post_text}
              onChange={(e) => setEditing((s) => ({ ...s, [p.id]: e.target.value }))}
              onBlur={() => save(p)}
              rows={6}
              className="rounded-none bg-canvas"
              data-testid={`gbp-text-${p.week_start}`}
            />
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Button onClick={() => copyPost(p)} className="btn-ink !rounded-none" data-testid={`gbp-copy-${p.week_start}`}>Copy post</Button>
              {p.status !== "posted" ? (
                <Button variant="outline" className="rounded-none" onClick={() => save(p, "posted")} data-testid={`gbp-mark-posted-${p.week_start}`}>Mark as posted</Button>
              ) : (
                <Button variant="outline" className="rounded-none" onClick={() => save(p, "draft")}>Back to draft</Button>
              )}
              <Button variant="ghost" className="rounded-none text-ink-muted" onClick={() => remove(p)} data-testid={`gbp-delete-${p.week_start}`}>Delete</Button>
              {p.image_suggestion && (
                <span className="font-mono-label text-ink-muted">Photo: {p.image_suggestion.split("/").pop()}</span>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
